// Task 105c D3 — Rotate credential modal.
//
// Opened from the API Credential card's Rotate button. Collects a new
// Insightful API key and submits to POST /credentials (first time) or
// PUT /credentials/rotate (existing). The modal is admin-only — parent
// task 105 Q9's RBAC gate on the credential card already hides this
// surface from members; the modal relies on that gate and does not
// re-check role itself.

import { useState, type FormEvent } from 'react';
import { Button } from '@/components/primitives/Button';
import { TextInput } from '@/components/primitives/TextInput';
import { SlidePanel } from '@/components/composites/SlidePanel';
import { useRotateCredential } from '@/hooks/useCredentialStatus';
import styles from './RotateCredentialModal.module.css';

interface RotateCredentialModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Whether the org already has a credential. `false` → POST (first-time
   * create). `true` → PUT /rotate (replace existing). Comes from
   * useCredentialStatus().data?.exists at the parent.
   */
  exists: boolean;
  /**
   * Mockup mode suppresses the network call and resolves the submit
   * immediately. Follows the PR #177 org-settings-save precedent.
   */
  mockupMode: boolean;
}

export function RotateCredentialModal({
  open,
  onClose,
  exists,
  mockupMode,
}: RotateCredentialModalProps) {
  const [value, setValue] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const rotate = useRotateCredential();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);
    if (!value.trim()) {
      setErrorMessage('API key cannot be empty.');
      return;
    }
    if (mockupMode) {
      // Matches the PR #177 org-settings-save mockup behavior: the
      // submit appears to succeed, no network call is made.
      setSuccessMessage(
        exists ? 'Credential rotated (mockup mode).' : 'Credential saved (mockup mode).',
      );
      setValue('');
      return;
    }
    try {
      await rotate.mutateAsync({ value: value.trim(), exists });
      setSuccessMessage(exists ? 'Credential rotated.' : 'Credential saved.');
      setValue('');
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'Unknown error';
      setErrorMessage(`Credential save failed: ${reason}`);
    }
  };

  const handleClose = () => {
    setValue('');
    setSuccessMessage(null);
    setErrorMessage(null);
    onClose();
  };

  const title = exists ? 'Rotate credential' : 'Set credential';
  const subtitle = exists
    ? 'Replace the existing Insightful API key with a new value.'
    : 'Add the Insightful API key for this organization.';

  return (
    <SlidePanel open={open} onClose={handleClose} title={title} subtitle={subtitle} width="sm">
      <form onSubmit={handleSubmit} className={styles.form}>
        <TextInput
          label="Insightful API key"
          type="password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="off"
          required
          disabled={rotate.isPending}
          hint="Stored encrypted at rest (pgcrypto). Never displayed in plaintext after save."
        />
        {successMessage && <p className={styles.success}>{successMessage}</p>}
        {errorMessage && <p className={styles.error}>{errorMessage}</p>}
        <div className={styles.actions}>
          <Button variant="secondary" size="md" type="button" onClick={handleClose}>
            {successMessage ? 'Close' : 'Cancel'}
          </Button>
          {!successMessage && (
            <Button variant="primary" size="md" type="submit" disabled={rotate.isPending}>
              {rotate.isPending ? 'Saving\u2026' : 'Save'}
            </Button>
          )}
        </div>
      </form>
    </SlidePanel>
  );
}
